import React, { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";

// GPU-cheap volumetric fire: additive points rising + flickering point light.
export function FireParticles({ position = [0, 0, 0], count = 90, scale = 1, intensity = 1 }) {
  const pts = useRef();
  const light = useRef();
  const data = useMemo(() => {
    const pos = new Float32Array(count * 3);
    const vel = new Float32Array(count);
    const off = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 0.7 * scale;
      pos[i * 3 + 1] = Math.random() * 1.2 * scale;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 0.7 * scale;
      vel[i] = 0.4 + Math.random() * 0.8;
      off[i] = Math.random() * 10;
    }
    return { pos, vel, off };
  }, [count, scale]);

  const geom = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(data.pos.slice(), 3));
    return g;
  }, [data]);

  const mat = useMemo(() => {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 64;
    const c = canvas.getContext("2d");
    const grd = c.createRadialGradient(32, 32, 0, 32, 32, 32);
    grd.addColorStop(0, "rgba(255,240,180,1)");
    grd.addColorStop(0.4, "rgba(255,150,40,0.8)");
    grd.addColorStop(1, "rgba(120,20,0,0)");
    c.fillStyle = grd;
    c.fillRect(0, 0, 64, 64);
    const tex = new THREE.CanvasTexture(canvas);
    return new THREE.PointsMaterial({
      size: 0.5 * scale,
      map: tex,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      opacity: 0.9 * intensity,
    });
  }, [scale, intensity]);

  useFrame((state, dt) => {
    if (!pts.current) return;
    const arr = pts.current.geometry.attributes.position.array;
    for (let i = 0; i < count; i++) {
      arr[i * 3 + 1] += data.vel[i] * dt * scale;
      if (arr[i * 3 + 1] > 1.6 * scale) {
        arr[i * 3 + 1] = 0;
        arr[i * 3] = (Math.random() - 0.5) * 0.7 * scale;
        arr[i * 3 + 2] = (Math.random() - 0.5) * 0.7 * scale;
      }
    }
    pts.current.geometry.attributes.position.needsUpdate = true;
    if (light.current) {
      const t = state.clock.elapsedTime;
      light.current.intensity =
        intensity * (2.4 + Math.sin(t * 18) * 0.7 + Math.sin(t * 7.3) * 0.5);
    }
  });

  return (
    <group position={position}>
      <points ref={pts} geometry={geom} material={mat} />
      <pointLight ref={light} color={0xff7a2a} distance={7 * scale} intensity={2.4 * intensity} position={[0, 0.7 * scale, 0]} />
    </group>
  );
}

// Time-limited sprinkler burst: blue-white droplets falling from the ceiling.
// Ignites when impactT first crosses the strike threshold, runs ~5s, then stops.
export function WaterSpray({ game, position = [0, 0, 0], count = 70, scale = 1, duration = 5 }) {
  const pts = useRef();
  const grp = useRef();
  const startedRef = useRef(-1);
  const data = useMemo(() => {
    const pos = new Float32Array(count * 3);
    const vel = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 1.6 * scale;
      pos[i * 3 + 1] = Math.random() * 1.4 * scale;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 1.6 * scale;
      vel[i] = 1.4 + Math.random() * 1.8;
    }
    return { pos, vel };
  }, [count, scale]);

  const geom = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(data.pos.slice(), 3));
    return g;
  }, [data]);

  const mat = useMemo(() => {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 64;
    const c = canvas.getContext("2d");
    const grd = c.createRadialGradient(32, 32, 0, 32, 32, 32);
    grd.addColorStop(0, "rgba(235,248,255,1)");
    grd.addColorStop(0.45, "rgba(150,200,240,0.7)");
    grd.addColorStop(1, "rgba(90,140,190,0)");
    c.fillStyle = grd;
    c.fillRect(0, 0, 64, 64);
    const tex = new THREE.CanvasTexture(canvas);
    return new THREE.PointsMaterial({
      size: 0.14 * scale,
      map: tex,
      transparent: true,
      depthWrite: false,
      blending: THREE.NormalBlending,
      opacity: 0.85,
    });
  }, [scale]);

  useFrame((state, dt) => {
    if (!pts.current || !grp.current) return;
    const t = game && game.current ? game.current.impactT || 0 : 0;
    if (startedRef.current < 0) {
      if (t >= 0.85) startedRef.current = state.clock.elapsedTime;
      grp.current.visible = false;
      return;
    }
    const elapsed = state.clock.elapsedTime - startedRef.current;
    if (elapsed > duration) { grp.current.visible = false; return; }
    grp.current.visible = true;
    const arr = pts.current.geometry.attributes.position.array;
    for (let i = 0; i < count; i++) {
      arr[i * 3 + 1] -= data.vel[i] * dt * scale;
      if (arr[i * 3 + 1] < -0.2 * scale) {
        arr[i * 3 + 1] = 1.4 * scale;
        arr[i * 3] = (Math.random() - 0.5) * 1.6 * scale;
        arr[i * 3 + 2] = (Math.random() - 0.5) * 1.6 * scale;
      }
    }
    pts.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <group ref={grp} position={position} visible={false}>
      <points ref={pts} geometry={geom} material={mat} />
    </group>
  );
}

// Live electrical wire — hangs, sparks intermittently.
export function ElectricWire({ position = [0, 0, 0], rotation = [0, 0, 0] }) {
  const spark = useRef();
  const light = useRef();
  const line = useMemo(() => {
    const curve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(-0.6, 0.2, 0),
      new THREE.Vector3(-0.2, -0.3, 0.05),
      new THREE.Vector3(0.2, -0.15, -0.05),
      new THREE.Vector3(0.6, -0.4, 0),
    ]);
    return new THREE.TubeGeometry(curve, 24, 0.02, 6, false);
  }, []);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const flash = Math.random() > 0.94;
    if (spark.current) spark.current.visible = flash;
    if (light.current) light.current.intensity = flash ? 3 : 0.0;
  });

  return (
    <group position={position} rotation={rotation}>
      <mesh geometry={line}>
        <meshStandardMaterial color={0x111111} roughness={0.6} metalness={0.4} />
      </mesh>
      <mesh ref={spark} position={[0.2, -0.15, 0]}>
        <sphereGeometry args={[0.06, 8, 8]} />
        <meshBasicMaterial color={0x9fd8ff} />
      </mesh>
      <pointLight ref={light} color={0x9fd8ff} distance={4} intensity={0} position={[0.2, -0.15, 0]} />
    </group>
  );
}
